import { useMemo, useCallback } from 'react';
import Cookies from 'universal-cookie';
import { useBucket } from '@context/bucket.context';
import { LDState } from '@components/bucketTest/types';
import { useAnalytics } from '@context/analytics.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import usePageType from '@hooks/use-page-type';
import useWelcomeVoucherQuery from '@hooks/user/use-welcome-voucher-query';
import MobileDetectionService from '@services/mobile-detection-service';
import useFeatureFlag from '@hooks/use-feature-flags';
import { PAGETYPE_TO_TOUCHPOINT } from '@constants/welcome-voucher';
import { ADJUST_LINK_BASES, BANNER_SIZE, BANNER_TYPE, BannerType, PAGETYPE_VARIANTS } from '@constants/smart-banner-v2';
import { useSmartBannerVisibilityManager } from '@hooks/smart-banner-v2/use-smart-banner-visibility-manager';
import { useFacebookAdjustTracking } from '@hooks/smart-banner-v2/use-facebook-adjust-tracking';
import { executeAfterPageviewIfSemPDP } from '@helpers/utils/analytics-utils';

export const useSmartBannerV2 = () => {
  const { getFeature, isFeatureEnabled, state: ldStatus } = useBucket();
  const { scriptsEventData } = useAnalytics();
  const { sendAnalyticEvent } = useAnalyticEvents('smart_banner');
  const { pageType } = usePageType();
  const { isSemPDP } = useFeatureFlag();
  const { welcomeVoucher } = useWelcomeVoucherQuery({
    touchPoint: isSemPDP && pageType === 'ProductPage' ? 'HERO_PDP' : PAGETYPE_TO_TOUCHPOINT[pageType],
  });
  const { visibilityState } = useSmartBannerVisibilityManager();
  const { isFbAdsAdjustTrackingEnabled, isFromFacebookAd, getFacebookAdjustUrl } = useFacebookAdjustTracking();

  const cookies = new Cookies();
  const adjCampaign = cookies.get('adjCampaign');
  const enableSmartBannerParameter = isFeatureEnabled((f) => f.smartBannerParameter, true);
  const bannerVariation: Partial<BannerType> = getFeature('appBannerVariations', true)?.value;

  const osList = useMemo(
    () => ({
      isIos: MobileDetectionService.isIOS(),
      isAndroid: MobileDetectionService.isAndroid(),
    }),
    [],
  );

  const bannerVariant = useMemo((): BannerType | '' => {
    if (!visibilityState.show) {
      return '';
    }

    if (visibilityState.variant === BANNER_SIZE.SMALL) {
      if (
        !welcomeVoucher ||
        (bannerVariation === 'commercial_precise' && welcomeVoucher?.discount?.formatted === '0')
      ) {
        return BANNER_TYPE.COMMERCIAL_PRECISE_FALLBACK;
      }
      return bannerVariation || BANNER_TYPE.CONTROL;
    }

    if (visibilityState.variant === BANNER_SIZE.BIG) {
      const hasValidVoucher = Boolean(welcomeVoucher && welcomeVoucher?.discount?.formatted !== '0');
      const variantFunc = PAGETYPE_VARIANTS[pageType];

      if (variantFunc) {
        return variantFunc(hasValidVoucher);
      }

      return BANNER_TYPE.APP_GENERAL_BIG;
    }

    return BANNER_TYPE.CONTROL;
  }, [visibilityState.show, visibilityState.variant, bannerVariation, welcomeVoucher, pageType]);

  const getAppUrl = (variantType: string): string => {
    if (typeof window === 'undefined') {
      return '';
    }

    // Check if we should use Facebook Adjust URL
    if (isFbAdsAdjustTrackingEnabled && isFromFacebookAd) {
      return getFacebookAdjustUrl();
    }

    const baseUrl = ADJUST_LINK_BASES[variantType] || ADJUST_LINK_BASES.control;

    const smartBannerParams = enableSmartBannerParameter
      ? '&adj_engagement_type=fallback_click&adj_label=smartbanner'
      : '';

    const campaignParam = adjCampaign ? `&adj_campaign=${adjCampaign}` : '';

    return `${baseUrl}${smartBannerParams}&adj_deep_link=com.vestiairecollective.vestiaire%3A%2F%2Faction%3Ftype%3Dwebbanner%26url%3D${encodeURIComponent(
      window.location.href,
    )}${campaignParam}`;
  };

  const trackBannerClose = () => {
    sendAnalyticEvent('banner_app_download_close', { property: bannerVariant });
  };

  const trackBannerOpen = () => {
    sendAnalyticEvent('banner_app_download_open', { property: bannerVariant });
  };

  const isLdReady = ldStatus === LDState.SUCCESS;
  const isSemPdpPageviewEnabled = isLdReady ? isFeatureEnabled((f) => f.enablePageviewStEventsQueue, false) : false;

  const trackBannerView = useCallback(() => {
    if (!visibilityState.show || !scriptsEventData?.isTcEvents1Loaded || !scriptsEventData?.isTcEvents7Loaded) {
      return;
    }

    const eventPayload = { property: bannerVariant };
    executeAfterPageviewIfSemPDP(
      () => {
        sendAnalyticEvent('banner_app_download_view', eventPayload);
      },
      isSemPDP,
      pageType,
      'banner_app_download_view',
      eventPayload,
      'smart_banner',
      isSemPdpPageviewEnabled,
    );
  }, [
    visibilityState.show,
    scriptsEventData?.isTcEvents1Loaded,
    scriptsEventData?.isTcEvents7Loaded,
    bannerVariant,
    isSemPDP,
    pageType,
    isSemPdpPageviewEnabled,
  ]);

  return {
    visibilityState,
    bannerVariant,
    osList,
    getAppUrl,
    trackBannerClose,
    trackBannerOpen,
    trackBannerView,
    welcomeVoucher,
  };
};
